import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, ShieldCheck, Truck, Package, Award, TreeDeciduous, Droplet } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { handleBuyClick } from "@/lib/buy";
import SustainabilityScore from "@/components/SustainabilityScore";
import MerchantChip from "@/components/MerchantChip";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AiSummary from "@/components/AiSummary";
import MethodologyPanel from "@/components/MethodologyPanel";
import { useAuth } from "@/context/AuthContext";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import useSEO from "@/hooks/useSEO";
import { SITE_URL } from "@/lib/siteUrl";
import Breadcrumbs from "@/components/Breadcrumbs";

const METRIC_LABELS = {
  carbon_footprint: { label: "Carbon Footprint", icon: <Leaf className="h-4 w-4" /> },
  packaging: { label: "Packaging", icon: <Package className="h-4 w-4" /> },
  materials: { label: "Materials", icon: <ShieldCheck className="h-4 w-4" /> },
  recyclability: { label: "Recyclability", icon: <Leaf className="h-4 w-4" /> },
  manufacturing: { label: "Manufacturing", icon: <Award className="h-4 w-4" /> },
  shipping: { label: "Shipping", icon: <Truck className="h-4 w-4" /> },
  certifications: { label: "Certifications", icon: <ShieldCheck className="h-4 w-4" /> },
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/products/${id}`);
      setData(data);
      setActiveImage(0);
      window.scrollTo(0, 0);
      trackEvent("PRODUCT_VIEW", { product_id: id });
      trackEvent("SUSTAINABILITY_SCORE_VIEW", { product_id: id });
    })();
  }, [id]);

  const handleBuy = async () => {
    if (product) await handleBuyClick(product, { user });
  };

  const product = data?.product;
  useSEO({
    title: product ? `${product.name} – Sustainability Score ${product.sustainability_score}/100 | Ecowallet` : "Product | Ecowallet",
    description: product ? `${product.brand} ${product.name}: AI Sustainability Score ${product.sustainability_score}/100. See per-metric reasoning, sustainable alternatives, cashback of ₹${product.cashback} and ${product.green_points} Green Points on Ecowallet.` : undefined,
    ogImage: product?.image,
    ogType: "product",
    keywords: product ? `${product.name} sustainability score, ${product.brand} sustainability, ${product.name} eco score, is ${product.name} sustainable, sustainable alternatives to ${product.name}` : null,
    jsonLd: product ? [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        image: product.gallery || [product.image],
        description: product.description,
        brand: { "@type": "Brand", name: product.brand },
        category: product.category,
        offers: {
          "@type": "Offer",
          price: product.price,
          priceCurrency: "INR",
          availability: "https://schema.org/InStock",
          seller: { "@type": "Organization", name: product.merchant },
        },
        aggregateRating: product.review_count > 0 ? {
          "@type": "AggregateRating",
          ratingValue: product.rating,
          reviewCount: product.review_count,
        } : undefined,
        additionalProperty: [
          { "@type": "PropertyValue", name: "Sustainability Score", value: `${product.sustainability_score}/100` },
          ...Object.entries(product.metrics || {}).map(([k, v]) => ({
            "@type": "PropertyValue", name: k.replace(/_/g, " "), value: `${v}/100`,
          })),
          ...(product.certifications || []).map((c) => ({
            "@type": "PropertyValue", name: "Certification", value: c,
          })),
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
          { "@type": "ListItem", position: 2, name: "Products", item: SITE_URL + "/products" },
          { "@type": "ListItem", position: 3, name: product.category, item: SITE_URL + "/products?category=" + encodeURIComponent(product.category) },
          { "@type": "ListItem", position: 4, name: product.name },
        ],
      },
    ] : null,
  });

  if (!data) return <div className="max-w-7xl mx-auto p-8">Loading...</div>;
  const { alternatives, price_history, methodology } = data;
  const best = alternatives?.[0];

  return (
    <TooltipProvider>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <Breadcrumbs items={[
        { label: "Products", to: "/products" },
        { label: product.category, to: `/products?category=${encodeURIComponent(product.category)}` },
        { label: product.name },
      ]} />
      {/* Gallery + core info */}
      <div className="grid lg:grid-cols-12 gap-10 mt-6">
        <div className="lg:col-span-7" data-testid="product-gallery">
          <div className="relative aspect-[4/3] rounded-3xl overflow-hidden bg-secondary card-premium">
            <img src={product.gallery[activeImage]} alt="" className="h-full w-full object-cover" />
            <div className="absolute top-4 right-4 bg-white/90 dark:bg-black/70 backdrop-blur-md rounded-full p-2">
              <SustainabilityScore score={product.sustainability_score} size={80} stroke={7} />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3 mt-4">
            {product.gallery.map((g, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                className={`aspect-square rounded-2xl overflow-hidden border-2 transition-all ${
                  activeImage === i ? "border-primary" : "border-transparent opacity-70 hover:opacity-100"
                }`}
              >
                <img src={g} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{product.brand} · {product.category}</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold leading-tight tracking-tight">{product.name}</h1>
          <div className="mt-4 flex items-center gap-3">
            <MerchantChip name={product.merchant} size="md" />
            <span className="text-sm text-muted-foreground">Sold via {product.merchant}</span>
          </div>

          <div className="flex items-baseline gap-3 mt-6">
            <span className="mono text-4xl font-bold">₹{product.price}</span>
            {product.original_price > product.price && (
              <span className="mono text-lg text-muted-foreground line-through">₹{product.original_price}</span>
            )}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="card-premium p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-widest">Cashback</div>
              <div className="mono text-xl font-bold text-emerald-600">₹{product.cashback}</div>
            </div>
            <div className="card-premium p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-widest">Green Points</div>
              <div className="mono text-xl font-bold text-amber-600">+{product.green_points}</div>
            </div>
          </div>

          <button onClick={handleBuy} className="btn-pill mt-6 w-full bg-primary text-primary-foreground" data-testid="buy-now-detail">
            Buy Now &amp; Earn <ArrowRight className="h-4 w-4 ml-1" />
          </button>

          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
            Choose products with stronger sustainability credentials and unlock greater Ecowallet rewards.
          </p>

          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{product.description}</p>

          <div className="mt-6 flex flex-wrap gap-2">
            {product.certifications.map((c) => (
              <span key={c} className="text-[11px] uppercase tracking-widest rounded-full border border-border px-3 py-1 bg-secondary">
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Sustainability Breakdown + Methodology (tabbed) */}
      <section className="mt-16">
        <Tabs defaultValue="summary">
          <TabsList className="mb-4">
            <TabsTrigger value="summary" data-testid="tab-summary">Why this score</TabsTrigger>
            <TabsTrigger value="breakdown" data-testid="tab-breakdown">Sustainability Breakdown</TabsTrigger>
            <TabsTrigger value="methodology" data-testid="tab-methodology">AI Methodology</TabsTrigger>
          </TabsList>
          <TabsContent value="summary">
            <AiSummary product={product} methodology={methodology} />
          </TabsContent>
          <TabsContent value="breakdown">
          <div className="card-premium p-6 sm:p-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Sustainability Breakdown</h2>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-sm text-muted-foreground mt-1 underline decoration-dotted cursor-help">
                  How is this score calculated?
                </p>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                This sustainability score is calculated using publicly available product information and our
                transparent methodology.
              </TooltipContent>
            </Tooltip>
          </div>
          <SustainabilityScore score={product.sustainability_score} size={110} stroke={9} />
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {Object.entries(product.metrics).map(([k, v]) => (
            <div key={k}>
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-2 text-sm font-medium">{METRIC_LABELS[k]?.icon} {METRIC_LABELS[k]?.label || k}</span>
                <span className="mono text-sm font-bold">{v}</span>
              </div>
              <Progress value={v} className="h-2" data-testid={`metric-${k}`} />
            </div>
          ))}
        </div>
          </div>
          </TabsContent>
          <TabsContent value="methodology">
            <MethodologyPanel product={product} methodology={methodology} />
          </TabsContent>
        </Tabs>
      </section>

      {/* Price History */}
      <section className="mt-10 card-premium p-6 sm:p-8">
        <h3 className="text-xl font-bold mb-4">30-day Price History</h3>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={price_history}>
              <XAxis dataKey="day" tickFormatter={(d) => `-${d}d`} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RTooltip />
              <Line type="monotone" dataKey="price" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Green Alternative Section */}
      {best && (
        <section className="mt-14" data-testid="green-alternatives">
          <div className="flex items-center gap-3 mb-6">
            <Leaf className="h-6 w-6 text-emerald-600" />
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Better Green Alternatives</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="card-premium p-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Current product</div>
              <div className="flex gap-4">
                <img src={product.image} className="h-24 w-24 rounded-2xl object-cover" />
                <div className="flex-1">
                  <div className="font-semibold">{product.name}</div>
                  <div className="text-sm text-muted-foreground">{product.brand}</div>
                  <div className="mt-2 flex items-center gap-3">
                    <SustainabilityScore score={product.sustainability_score} size={54} stroke={5} />
                    <span className="mono text-lg font-bold">₹{product.price}</span>
                  </div>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              className="card-premium p-6 border-2 border-emerald-500/40 bg-emerald-50/30 dark:bg-emerald-900/10"
            >
              <div className="text-xs uppercase tracking-widest text-emerald-700 dark:text-emerald-300 mb-3 font-bold">
                ✨ Greener alternative · +{best.sustainability_score - product.sustainability_score} pts higher
              </div>
              <div className="flex gap-4">
                <img src={best.image} className="h-24 w-24 rounded-2xl object-cover" />
                <div className="flex-1">
                  <Link to={`/product/${best.id}`} className="font-semibold hover:underline">{best.name}</Link>
                  <div className="text-sm text-muted-foreground">{best.brand}</div>
                  <div className="mt-2 flex items-center gap-3">
                    <SustainabilityScore score={best.sustainability_score} size={54} stroke={5} />
                    <span className="mono text-lg font-bold">₹{best.price}</span>
                  </div>
                </div>
              </div>
              <ul className="mt-4 space-y-1.5 text-sm text-foreground/80">
                <li className="flex gap-2"><Leaf className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Uses recycled / natural materials</li>
                <li className="flex gap-2"><TreeDeciduous className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Lower carbon footprint ({best.carbon_saved_kg} kg CO₂ saved)</li>
                <li className="flex gap-2"><Package className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Plastic-free packaging</li>
                <li className="flex gap-2"><Droplet className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Saves {best.water_saved_l}L water</li>
              </ul>
              <Link to={`/product/${best.id}`} className="btn-pill mt-4 bg-emerald-600 text-white w-full">
                Switch to this alternative →
              </Link>
            </motion.div>
          </div>
        </section>
      )}
    </div>
    </TooltipProvider>
  );
};

export default ProductDetailPage;
